import React, { useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import { NavLink, useLocation } from "react-router-dom";

const iconSx = { fontSize: 18 };

const itemBase =
  "group relative flex h-[44px] items-center text-[13px] font-medium capitalize tracking-[0.01em] transition-all duration-200 ease-out";

function iconWrapClass(active: boolean) {
  return [
    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-all duration-200",
    active
      ? "bg-white/15 text-erp-gold"
      : "bg-white/[0.04] text-[#8fa6bb] group-hover:bg-white/[0.08] group-hover:text-erp-gold",
  ].join(" ");
}

function BranchSideBarItem({
  item,
  onNavigate,
  collapsed = false,
}: {
  item: any;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const location = useLocation();
  const Icon = item?.ICON || MenuIcon;
  const visibleSubnav =
    item?.SUBNAV?.filter((sub: any) => !sub?.HIDE && sub?.ELEMENT) || [];
  const hasDropdown = visibleSubnav.length > 0 && item?.DIRECT === undefined;
  const childActive = visibleSubnav.some(
    (sub: any) =>
      location.pathname === sub?.PATH ||
      location.pathname.startsWith(`${sub?.PATH}/`),
  );
  const [open, setOpen] = useState<boolean>(childActive);

  if (hasDropdown) {
    if (collapsed) {
      const firstPath = visibleSubnav[0]?.PATH;
      return (
        <li className="w-full px-1.5">
          <NavLink
            to={firstPath || "#"}
            title={item?.TITLE}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                itemBase,
                "w-full justify-center rounded-lg",
                isActive || childActive
                  ? "bg-erp-accent/90 text-white shadow-[0_6px_16px_rgba(31,111,139,0.28)]"
                  : "text-[#a9bdd0] hover:bg-white/[0.06] hover:text-white",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <span className={iconWrapClass(isActive || childActive)}>
                <Icon sx={iconSx} />
              </span>
            )}
          </NavLink>
        </li>
      );
    }

    return (
      <li className="w-full px-2">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={[
            itemBase,
            "w-full justify-between rounded-lg border-0 bg-transparent px-2.5 text-left",
            open || childActive
              ? "bg-white/[0.05] text-white"
              : "text-[#a9bdd0] hover:bg-white/[0.06] hover:text-white",
          ].join(" ")}
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <span className={iconWrapClass(open || childActive)}>
              <Icon sx={iconSx} />
            </span>
            <span className="truncate">{item?.TITLE}</span>
          </span>
          <KeyboardArrowDownIcon
            className={`shrink-0 text-[#7f96ad] transition-transform duration-200 ${
              open ? "rotate-0" : "-rotate-90"
            }`}
            sx={{ fontSize: 18 }}
          />
        </button>
        <ul
          className={`mt-0.5 overflow-hidden pl-3 transition-[max-height,opacity] duration-300 ease-out ${
            open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          {visibleSubnav.map((subItem: any, j: number) => (
            <li key={j} className="w-full">
              <NavLink
                to={`${subItem?.PATH}`}
                onClick={onNavigate}
                className={({ isActive }) =>
                  [
                    "relative my-0.5 flex h-[38px] items-center rounded-md pl-8 pr-2.5 text-[12.5px] font-medium capitalize tracking-[0.01em] transition-all duration-200",
                    isActive
                      ? "bg-erp-accent/20 text-white"
                      : "text-[#8ea3b8] hover:bg-white/[0.05] hover:text-white",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={[
                        "absolute left-3.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full transition-colors duration-200",
                        isActive
                          ? "bg-erp-gold shadow-[0_0_0_3px_rgba(201,162,77,0.2)]"
                          : "bg-[#4d6478]",
                      ].join(" ")}
                    />
                    <span className="truncate">{subItem?.TITLE}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  if (item?.SUBNAV?.length === 0 && item?.ELEMENT === undefined) {
    return null;
  }

  return (
    <li className={`w-full ${collapsed ? "px-1.5" : "px-2"}`}>
      <NavLink
        to={item?.PATH}
        title={item?.TITLE}
        onClick={onNavigate}
        className={({ isActive }) =>
          [
            itemBase,
            collapsed
              ? "w-full justify-center rounded-lg"
              : "w-full gap-2.5 rounded-lg px-2.5",
            isActive
              ? "bg-gradient-to-r from-erp-accent to-erp-accent-bright text-white shadow-[0_6px_16px_rgba(31,111,139,0.28)]"
              : "text-[#a9bdd0] hover:bg-white/[0.06] hover:text-white",
          ].join(" ")
        }
      >
        {({ isActive }) => (
          <>
            <span className={iconWrapClass(isActive)}>
              <Icon sx={iconSx} />
            </span>
            {!collapsed ? <span className="truncate">{item?.TITLE}</span> : null}
            {isActive && !collapsed ? (
              <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-erp-gold" />
            ) : null}
          </>
        )}
      </NavLink>
    </li>
  );
}

export default BranchSideBarItem;
